import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import { API, EXTRAS_META, quote, bikeIsRideable, BIKE_STATUS } from "../config";
import SiempriaCredit from "../components/SiempriaCredit";
import { LogOut, RefreshCw, Plus, X, Loader2, Wrench, CheckCircle2, PiggyBank, Wallet, BadgeCheck, CalendarDays, BookOpen, FileText, Target } from "lucide-react";
import HelpModal from "../components/HelpModal";

const EXTRA_KEYS = Object.keys(EXTRAS_META);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const bikeImg = (b) => (b.image_path ? `${API}/bikes/image/${b.slug}` : b.image);
const fmt = (n) => `${Number(n || 0).toFixed(2).replace(/\.00$/, "")}€`;

function CommissionBadge({ paid }) {
  return paid ? (
    <span className="badge-brutal bg-topacio-green text-white"><BadgeCheck className="w-3 h-3" /> Cobrada</span>
  ) : (
    <span className="badge-brutal bg-brand text-topacio-ink"><Wallet className="w-3 h-3" /> Pendiente</span>
  );
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [bikes, setBikes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [statementBusy, setStatementBusy] = useState(false);

  const downloadStatement = async () => {
    setStatementBusy(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
      const res = await axios.get(`${API}/partner/statement`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "mi-informe-mensual.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Informe mensual descargado");
    } catch { toast.error("No se pudo generar el informe"); } finally { setStatementBusy(false); }
  };
  const [partnerName, setPartnerName] = useState("");
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [slug, setSlug] = useState("");
  const [range, setRange] = useState();
  const [extrasSel, setExtrasSel] = useState([]);
  const [pickup, setPickup] = useState("office");
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [hotelName, setHotelName] = useState("");
  const [hotelOpen, setHotelOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [doneRef, setDoneRef] = useState(null);
  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const [f, b, s, me, settings] = await Promise.all([
        axios.get(`${API}/partner/fleet`, { headers }),
        axios.get(`${API}/partner/bookings`, { headers }),
        axios.get(`${API}/partner/stats`, { headers }),
        axios.get(`${API}/auth/me`, { headers }),
        axios.get(`${API}/settings/public`),
      ]);
      setBikes(f.data.bikes);
      setBookings(b.data.bookings);
      setStats(s.data);
      setPartnerName(me.data.partner_name || me.data.email);
      const enabled = !!settings.data.delivery_pickup_enabled;
      setDeliveryEnabled(enabled);
      if (!enabled) {
        setPickup("office");
        setHotelName("");
        setHotelOpen(false);
      }
    } catch {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(() => bikes.find((b) => b.slug === slug) || null, [bikes, slug]);
  const available = bikes.filter((b) => bikeIsRideable(b) && !b.coming_soon);
  const days = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    const diff = Math.round((range.to - range.from) / 86400000);
    return diff > 0 ? diff : 1;
  }, [range]);
  const effectiveExtras = useMemo(
    () => (pickup === "hotel" && !extrasSel.includes("delivery") ? [...extrasSel, "delivery"] : extrasSel),
    [pickup, extrasSel]
  );
  const price = useMemo(
    () => (selected && days > 0 ? quote(selected, days, effectiveExtras, null) : null),
    [selected, days, effectiveExtras]
  );

  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysArr = [{ before: today }];
    (selected?.booked || []).forEach((r) => {
      let d = parseISO(r.start_date);
      const end = parseISO(r.end_date);
      while (d < end) {
        daysArr.push(new Date(d));
        d.setDate(d.getDate() + 1);
      }
    });
    return daysArr;
  }, [selected]);

  const submit = async () => {
    if (!selected || !range?.from || !range?.to || !form.name || !form.email || !form.phone) {
      toast.error("Completa todos los campos y las fechas");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/partner/bookings`, {
        bike_slug: selected.slug,
        start_date: iso(range.from), end_date: iso(range.to),
        pickup, hotel_name: pickup === "hotel" ? hotelName.trim() : null,
        extras: effectiveExtras, ...form,
      }, { headers });
      setDoneRef(data.booking.ref);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "dates_unavailable") toast.error("Esas fechas ya están ocupadas para esa moto");
      else if (d === "bike_unavailable") toast.error("Esa moto está en taller");
      else toast.error("Error al crear la reserva");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const paidPct = stats && stats.commission > 0 ? Math.round((stats.commission_paid / stats.commission) * 100) : 0;

  const inputCls = "w-full bg-white border-2 border-topacio-ink rounded-lg px-4 py-3.5 text-base sm:text-sm text-topacio-ink outline-none focus:border-brand focus:shadow-[3px_3px_0_0_#FFCC00] transition-[box-shadow,border-color] placeholder:text-zinc-400";
  const labelCls = "block text-xs uppercase tracking-wider text-topacio-faded font-extrabold mb-2";

  return (
    <div className="min-h-screen bg-topacio-sand" data-testid="partner-dashboard">
      <header className="border-b-2 border-topacio-ink bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-topacio.jpg" alt="Topacio" className="h-9 w-auto rounded border-2 border-topacio-ink shrink-0" />
            <span className="font-display font-bold text-xs sm:text-sm text-topacio-ink truncate">COLABORADOR · {partnerName}</span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button data-testid="help-btn" onClick={() => setHelpOpen(true)} className="text-topacio-faded hover:text-topacio-ink transition-colors p-2" title="Manual de usuario">
              <BookOpen className="w-4 h-4" />
            </button>
            <button data-testid="partner-refresh-btn" onClick={load} className="text-topacio-faded hover:text-topacio-ink transition-colors p-2">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button data-testid="partner-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs font-bold text-topacio-faded hover:text-topacio-ink transition-colors p-2">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            {[
              ["Reservas", stats.total, "partner-stat-total"],
              ["Activas", stats.active, "partner-stat-active"],
              ["Volumen", fmt(stats.volume), "partner-stat-volume"],
              ["Comisiones", fmt(stats.commission), "partner-stat-commission"],
            ].map(([label, value, tid]) => (
              <div key={tid} data-testid={tid} className="card-brutal px-4 sm:px-6 py-4 sm:py-5">
                <div className="font-display font-black text-xl sm:text-2xl text-topacio-ink">{value}</div>
                <div className="text-[10px] sm:text-[11px] text-topacio-faded uppercase tracking-wider font-bold mt-1">{label}</div>
              </div>
            ))}
          </div>
        )}

        {stats && (
          <section data-testid="commission-panel" className="card-brutal overflow-hidden mb-10">
            <div className="bg-topacio-ink px-5 sm:px-7 py-4 flex items-center justify-between">
              <h2 className="font-display font-bold text-white text-sm sm:text-base flex items-center gap-2.5 uppercase tracking-wide">
                <PiggyBank className="w-5 h-5 text-brand" /> Mis comisiones
              </h2>
              <span className="font-hand text-xl text-brand -rotate-2 select-none hidden sm:inline">¡cada reserva suma!</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y-2 sm:divide-y-0 sm:divide-x-2 divide-dashed divide-zinc-200">
              <div data-testid="commission-pending-card" className="p-6 bg-brand/40">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-extrabold text-topacio-faded">
                  <Wallet className="w-4 h-4 text-topacio-red" /> Pendiente de cobro
                </div>
                <div className="font-display font-black text-3xl sm:text-4xl text-topacio-red mt-2">{fmt(stats.commission_pending)}</div>
              </div>
              <div data-testid="commission-paid-card" className="p-6">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-extrabold text-topacio-faded">
                  <BadgeCheck className="w-4 h-4 text-topacio-green" /> Cobrado
                </div>
                <div className="font-display font-black text-3xl sm:text-4xl text-topacio-green mt-2">{fmt(stats.commission_paid)}</div>
              </div>
              <div data-testid="commission-total-card" className="p-6">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-extrabold text-topacio-faded">
                  <PiggyBank className="w-4 h-4 text-topacio-ink" /> Total generado
                </div>
                <div className="font-display font-black text-3xl sm:text-4xl text-topacio-ink mt-2">{fmt(stats.commission)}</div>
              </div>
            </div>
            <div className="px-5 sm:px-7 pb-6">
              <div className="flex justify-between text-[10px] uppercase tracking-wider font-extrabold text-topacio-faded mb-1.5">
                <span>Liquidado {paidPct}%</span>
                <span>Pendiente {100 - paidPct}%</span>
              </div>
              <div data-testid="commission-bar" className="h-4 border-2 border-topacio-ink rounded-full overflow-hidden bg-white">
                <div className="h-full bg-topacio-green transition-all duration-700" style={{ width: `${paidPct}%` }} />
              </div>
              {stats.monthly_goal > 0 && (
                <div data-testid="goal-panel" className="mt-5">
                  <div className="flex justify-between text-[10px] uppercase tracking-wider font-extrabold text-topacio-faded mb-1.5">
                    <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-topacio-red" /> Objetivo del mes</span>
                    <span>{stats.month_bookings} de {stats.monthly_goal} reservas</span>
                  </div>
                  <div data-testid="goal-bar" className="h-4 border-2 border-topacio-ink rounded-full overflow-hidden bg-white">
                    <div className={`h-full transition-all duration-700 ${stats.month_bookings >= stats.monthly_goal ? "bg-topacio-green" : "bg-brand"}`}
                      style={{ width: `${Math.min(100, Math.round((stats.month_bookings / stats.monthly_goal) * 100))}%` }} />
                  </div>
                  {stats.month_bookings >= stats.monthly_goal && (
                    <p data-testid="goal-reached" className="text-[11px] font-extrabold text-topacio-green mt-1.5 uppercase tracking-wider">
                      ¡Objetivo conseguido!{stats.goal_bonus_percent > 0 ? ` +${stats.goal_bonus_percent}% de comisión extra en tus reservas de este mes` : " Enhorabuena"}
                    </p>
                  )}
                </div>
              )}
              <p className="text-[11px] text-topacio-faded mt-3 font-semibold">Topacio liquida las comisiones pendientes cada mes. Verás cada reserva marcada como «Cobrada» cuando se complete el pago.</p>
              <button data-testid="partner-statement-btn" onClick={downloadStatement} disabled={statementBusy}
                className="mt-4 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wider border-2 border-topacio-ink rounded-lg bg-white px-3.5 py-2 text-topacio-ink hover:bg-brand hover:-translate-y-0.5 transition-all">
                {statementBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} Informe del mes pasado (PDF)
              </button>
            </div>
          </section>
        )}

        <div className="flex justify-between items-center mb-6 gap-3">
          <h2 className="font-display text-lg sm:text-xl font-bold text-topacio-ink">Flota en tiempo real</h2>
          <button data-testid="partner-new-booking-btn" onClick={() => { setOpen(true); setDoneRef(null); setRange(undefined); setSlug(""); setExtrasSel([]); setForm({ name: "", email: "", phone: "", message: "" }); }}
            className="btn-primary !px-4 sm:!px-5 !py-2.5 !text-xs shrink-0">
            <Plus className="w-4 h-4" /> Nueva reserva
          </button>
        </div>

        {/* Flota: tarjetas en móvil, tabla en desktop */}
        <div className="md:hidden space-y-3 mb-10">
          {bikes.map((bk) => (
            <div key={bk.slug} data-testid={`partner-bike-${bk.slug}`} className="card-brutal p-4 flex gap-4">
              <img src={bikeImg(bk)} alt={bk.name} onError={(e) => { e.currentTarget.src = "/logo-topacio.jpg"; }} className="w-20 h-16 object-cover rounded border-2 border-topacio-ink bg-white shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-bold text-topacio-ink text-sm leading-tight">{bk.name}</span>
                  <span className="font-display font-black text-topacio-red text-sm shrink-0">{bk.price_1d}€</span>
                </div>
                <div className="mt-1.5">
                  {!bikeIsRideable(bk) ? (
                    <span className={`badge-brutal ${(BIKE_STATUS[bk.status || "ok"] || {}).cls || "bg-topacio-red text-white"}`}><Wrench className="w-3 h-3" /> {(BIKE_STATUS[bk.status || "ok"] || {}).label || "No disponible"}</span>
                  ) : (
                    <span className="badge-brutal bg-topacio-green text-white"><CheckCircle2 className="w-3 h-3" /> Disponible</span>
                  )}
                </div>
                <p className="text-[10px] text-topacio-faded font-mono mt-2 truncate">
                  {(bk.booked || []).length === 0 ? "Sin reservas próximas" : bk.booked.map((r) => `${r.start_date}→${r.end_date}`).join(" · ")}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="card-brutal overflow-x-auto mb-12 hidden md:block">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                {["Moto", "Precio/día", "Estado", "Próximas reservas"].map((h) => (
                  <th key={h} className="px-5 py-4 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {bikes.map((bk) => (
                <tr key={bk.slug} data-testid={`partner-bike-${bk.slug}`} className="hover:bg-topacio-sand transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <img src={bikeImg(bk)} alt={bk.name} onError={(e) => { e.currentTarget.src = "/logo-topacio.jpg"; }} className="w-14 h-10 object-cover rounded border border-zinc-200 bg-white" />
                      <span className="font-bold text-topacio-ink">{bk.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-semibold">{bk.price_1d}€</td>
                  <td className="px-5 py-3">
                    {bk.status === "workshop" ? (
                      <span className="badge-brutal bg-topacio-red text-white"><Wrench className="w-3 h-3" /> Taller</span>
                    ) : (
                      <span className="badge-brutal bg-topacio-green text-white"><CheckCircle2 className="w-3 h-3" /> Disponible</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-topacio-faded font-mono">
                    {(bk.booked || []).length === 0 ? "Libre" : bk.booked.map((r) => `${r.start_date}→${r.end_date}`).join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="font-display text-lg sm:text-xl font-bold text-topacio-ink mb-6">Mis reservas</h2>

        {/* Reservas: tarjetas en móvil, tabla en desktop */}
        <div className="md:hidden space-y-3">
          {bookings.map((bk) => (
            <div key={bk.ref} data-testid={`partner-booking-${bk.ref}`} className="card-brutal p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-topacio-red font-extrabold text-sm">{bk.ref}</span>
                <span className={`text-[10px] font-extrabold uppercase ${bk.status === "cancelled" ? "text-topacio-red" : "text-topacio-green"}`}>{bk.status}</span>
              </div>
              <div className="mt-2 text-sm font-bold text-topacio-ink">{bk.name}</div>
              <div className="text-xs text-topacio-faded">{bk.phone}</div>
              <div className="mt-2 flex items-center gap-2 text-xs text-topacio-faded font-semibold">
                <CalendarDays className="w-3.5 h-3.5" /> {bk.start_date} → {bk.end_date}
              </div>
              <div className="mt-3 pt-3 border-t-2 border-dashed border-zinc-200 flex items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="font-extrabold text-topacio-ink">{fmt(bk.total)}</span>
                  <span className="text-topacio-faded"> · comisión </span>
                  <span className="font-extrabold text-topacio-green">{fmt(bk.commission_eur)}</span>
                </div>
                <span data-testid={`commission-badge-${bk.ref}`}><CommissionBadge paid={!!bk.commission_paid} /></span>
              </div>
            </div>
          ))}
          {bookings.length === 0 && (
            <div className="card-brutal p-10 text-center text-topacio-faded text-sm">Aún no has creado reservas.</div>
          )}
        </div>
        <div className="card-brutal overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                {["Ref", "Cliente", "Moto", "Fechas", "Total", "Comisión", "Liquidación", "Estado"].map((h) => (
                  <th key={h} className="px-5 py-4 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {bookings.map((bk) => (
                <tr key={bk.ref} data-testid={`partner-booking-${bk.ref}`} className="hover:bg-topacio-sand transition-colors">
                  <td className="px-5 py-4 font-mono text-topacio-red font-extrabold">{bk.ref}</td>
                  <td className="px-5 py-4">
                    <div className="text-topacio-ink font-bold">{bk.name}</div>
                    <div className="text-xs text-topacio-faded">{bk.phone}</div>
                  </td>
                  <td className="px-5 py-4 font-semibold text-topacio-ink">{bk.bike_name}</td>
                  <td className="px-5 py-4 text-topacio-faded whitespace-nowrap">{bk.start_date} → {bk.end_date}</td>
                  <td className="px-5 py-4 font-extrabold text-topacio-ink">{bk.total}€</td>
                  <td className="px-5 py-4 font-extrabold text-topacio-green">{bk.commission_eur || 0}€</td>
                  <td className="px-5 py-4"><span data-testid={`commission-badge-${bk.ref}`}><CommissionBadge paid={!!bk.commission_paid} /></span></td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-extrabold uppercase ${bk.status === "cancelled" ? "text-topacio-red" : "text-topacio-green"}`}>
                      {bk.status}
                    </span>
                  </td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-12 text-center text-topacio-faded">Aún no has creado reservas.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <SiempriaCredit />
      </main>

      {open && (
        <div className="fixed inset-0 z-[90] flex items-stretch sm:items-center justify-center p-0 sm:p-4" data-testid="partner-booking-modal">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative card-brutal !rounded-none sm:!rounded-xl w-full sm:max-w-xl h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto pb-24 sm:pb-6" data-lenis-prevent>
            <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b-2 border-topacio-ink sticky top-0 bg-white z-10">
              <h3 className="font-display font-bold text-topacio-ink">Nueva reserva</h3>
              <button data-testid="partner-modal-close" onClick={() => setOpen(false)} className="text-topacio-faded hover:text-topacio-ink p-1"><X className="w-5 h-5" /></button>
            </div>
            {doneRef ? (
              <div className="text-center py-10 px-6">
                <CheckCircle2 className="w-14 h-14 text-topacio-green mx-auto" />
                <p className="font-display font-bold text-topacio-ink text-lg mt-4">Reserva confirmada</p>
                <p data-testid="partner-booking-ref" className="font-display font-black text-topacio-red text-2xl mt-3">{doneRef}</p>
                <button data-testid="partner-done-btn" onClick={() => setOpen(false)} className="btn-primary mt-6 !px-8">OK</button>
              </div>
            ) : (
              <div className="space-y-5 p-5 sm:p-6">
                <div>
                  <label className={labelCls}>Moto</label>
                  <select data-testid="partner-bike-select" value={slug} onChange={(e) => { setSlug(e.target.value); setRange(undefined); }} className={inputCls}>
                    <option value="">—</option>
                    {available.map((b) => (
                      <option key={b.slug} value={b.slug}>{b.name} — {b.price_1d}€/día</option>
                    ))}
                  </select>
                </div>
                {selected && (
                  <div>
                    <label className={labelCls}>Fechas</label>
                    <div className="border-2 border-topacio-ink rounded-lg p-2 sm:p-3 bg-white flex justify-center overflow-x-auto" data-testid="partner-calendar">
                      <DayPicker mode="range" selected={range} onSelect={setRange} disabled={disabledDays} locale={es} numberOfMonths={1} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Nombre del cliente</label>
                    <input data-testid="partner-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input data-testid="partner-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Teléfono</label>
                    <input data-testid="partner-phone-input" inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Punto de recogida</label>
                  {deliveryEnabled ? (
                    <>
                      <select data-testid="partner-pickup-select" value={pickup}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "hotel" && !hotelName) setHotelOpen(true);
                          else { setPickup(v); if (v !== "hotel") setHotelName(""); }
                        }} className={inputCls}>
                        <option value="office">Oficina Costa Adeje</option>
                        <option value="airport">Aeropuerto TFS</option>
                        <option value="hotel">Entrega en hotel (+25 €)</option>
                      </select>
                      {pickup === "hotel" && hotelName && (
                        <button type="button" data-testid="partner-hotel-edit-btn" onClick={() => setHotelOpen(true)}
                          className="mt-2 text-xs font-extrabold text-topacio-ink bg-brand border-2 border-topacio-ink rounded-lg px-3 py-2 shadow-[2px_2px_0_0_#111]">
                          Entrega en hotel: {hotelName}
                        </button>
                      )}
                    </>
                  ) : (
                    <div data-testid="partner-office-only" className={`${inputCls} bg-topacio-sand font-bold text-topacio-ink`}>
                      Oficina Costa Adeje · única opción activa
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {EXTRA_KEYS.filter((k) => k !== "delivery").map((k) => {
                    const active = extrasSel.includes(k);
                    return (
                      <button key={k} data-testid={`partner-extra-${k}`}
                        onClick={() => setExtrasSel((p) => (p.includes(k) ? p.filter((e) => e !== k) : [...p, k]))}
                        className={`px-3.5 py-2.5 text-xs font-bold rounded-lg border-2 transition-colors ${active ? "border-topacio-ink bg-brand text-topacio-ink" : "border-zinc-200 text-topacio-faded"}`}>
                        {k}
                      </button>
                    );
                  })}
                </div>
                {/* barra de acción sticky en móvil */}
                <div className="sticky bottom-0 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 mt-6 px-5 sm:px-6 py-4 bg-white border-t-2 border-topacio-ink">
                  {price && (
                    <div data-testid="partner-price" className="flex justify-between font-display font-black text-topacio-ink text-base mb-3">
                      <span>Total ({days} días)</span><span className="text-topacio-red">{price.total}€</span>
                    </div>
                  )}
                  <button data-testid="partner-submit-btn" disabled={busy} onClick={submit} className="btn-primary w-full !py-4 sm:!py-3.5">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                    Confirmar reserva
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {hotelOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="partner-hotel-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => { if (pickup !== "hotel") setHotelOpen(false); }} />
          <div className="relative card-brutal w-full max-w-sm p-6">
            <h4 className="font-display font-bold text-topacio-ink mb-2">¿En qué hotel se aloja el cliente?</h4>
            <p className="text-xs text-topacio-faded font-semibold mb-4">Llevaremos la moto a la recepción de ese hotel (+25 €).</p>
            <input data-testid="partner-hotel-name-input" value={hotelName} onChange={(e) => setHotelName(e.target.value)}
              placeholder="Nombre del hotel o apartamento" className={inputCls} autoFocus />
            <div className="flex gap-3 mt-5">
              <button type="button" data-testid="partner-hotel-cancel" onClick={() => { setHotelOpen(false); if (!hotelName) setPickup("office"); }}
                className="btn-secondary flex-1 !py-3 !text-xs">Atrás</button>
              <button type="button" data-testid="partner-hotel-confirm"
                onClick={() => {
                  if (!hotelName.trim()) { toast.error("Dinos el nombre del hotel"); return; }
                  setPickup("hotel");
                  setHotelOpen(false);
                }}
                className="btn-primary flex-1 !py-3 !text-xs">Confirmar entrega</button>
            </div>
          </div>
        </div>
      )}
      {helpOpen && <HelpModal role="partner" onClose={() => setHelpOpen(false)} />}
    </div>
  );
}
